/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { intersectArrays } from 'util/ArrayOperations';
/**
 * computes the domain from the measures in stats and set notify widget id if needed
 * @param {Object} measures - measures from stats data
 * @param {Object} existingDomain - cached domain from compared model
 * eg. existing domain - note dataMax and ScaleMax can be different. Same with dataMin and ScaleMin
 * {
 *      widgetId1: {
 *          Quantityavg: {
 *		 		dataMax: xx,
 *              dataMin: xx,
 *		 	    (Scale) max: xx,
 *              (Scale) min: xx
 *		  	},
 *          UnitCostsum: {
 *              dataMax: xx,
 *		 	    dataMin: xx,
 *		 	    (Scale) max: xx,
 *              (Scale) min: xx
 *          }
 *      }
 * }
 * @param {String} widgetApi - the widget api
 * @param {Array<String>} widgetIds - the list of widget ids in compare model
 * @param {boolean} isForcedByUser - a flag to indicate if the matching scale operation is forced by user
 * @returns {Object} result
 * eg.{ notifyWidgetId: xx,
 *		domain: {
 *			widgetId1: {
 *			{
 *					Quantityavg: {
 *		 				dataMax: xx,
 *		 				dataMin: xx,
 *						(Scale) max: xx,
 *						(Scale) min: xx
 *					}, {
 *					UnitCostsum: {
 *						dataMax: xx,
 *						dataMin: xx,
 *						(Scale) max: xx,
 *						(Scale) min: xx
 * 					}
 *		  		 }
 *           },
 *			widgetId2: {
 *			{
 *					Quantityavg: {
 *		 				dataMax: xx,
 *		 				dataMin: xx,
 *						(Scale) max: xx,
 *						(Scale) min: xx
 *					}, {
 *					Marginsum: {
 *						dataMax: xx,
 *						dataMin: xx,
 *						(Scale) max: xx,
 *						(Scale) min: xx
 * 					}
 *		  		 }
 *           }
 *			}
 */
function getDomainAndNotifyWidgetId( measures, existingDomain, widgetContentApi, widgetIds, isForcedByUser) {
	const widgetContentId = widgetContentApi && widgetContentApi.getId();
	const otherWidgetId = _findTargetWidgetId(widgetContentId, widgetIds);
	/* first check matching scale eligibility at widgets level -> then we check matching scale eligibility at slot level
	eg. bar chart - we should match scale for all of the measures if applies; 
	tree map - we should not match scale unless same data items are used in heat slots are filled for both widgets
	*/
	const isWidgetEligible = _isWidgetEligibleForMatchingScale(widgetContentApi);
	if(isWidgetEligible){
		return _computeDomainAndNotifyWidgetId(measures, existingDomain, widgetContentApi, otherWidgetId, undefined /**dataItemItemIds */, isForcedByUser);
	} else {
		const dataItemItemIds = _findDataItemsInEligibleSlotsForMatchingScale(widgetContentApi);
		if(dataItemItemIds.length){
			return _computeDomainAndNotifyWidgetId(measures, existingDomain, widgetContentApi, otherWidgetId, dataItemItemIds, isForcedByUser);
		} else {
			return _cleanUpUnprojectedDataItemsFromDomain(measures, existingDomain, widgetContentId, otherWidgetId, undefined/*notifyWidgetId*/, widgetContentApi);
		}
	}
}

/**
 * get the target widget id from the source widget id and the ids array
 * @param {String} sourceWidgetId - the source widget id
 * @param {Array<String>} widgetIds - the array of the widget ids
 * @returns {String} the id of target widget id
 */
function _findTargetWidgetId (sourceWidgetId, widgetIds){
	return widgetIds.find(widgetId => widgetId !== sourceWidgetId);
}

/**
 * determine if we should compute scale
 * a) If two visualizations have the same measure projected, ensure that the axis scales only synchronize if the measure is in the same slot.
 * b) If the other visualization is composite chart and the current slotId is values
 * @param {Object} targetDomain - the domain of the other widget
 *	eg. 
 * 	{targetWidgetId: {
 *		Quantityavg: {
 *		 	dataMax: xx,
 *		 	dataMin: xx,
 *			(Scale) max: xx,
 *			(Scale) min: xx
 *			}, {
 *			UnitCostsum: {
 *				dataMax: xx,
 *				dataMin: xx,
 *				(Scale) max: xx,
 *				(Scale) min: xx
 * 			}
 * }}
 * @param {String} itemKey - the item key of from the column in the source widget. ItemKey is item id plus aggregation type plus layerId. eg. 'revenueSumData'
 * @param {String} itemSlotId - the item slot id from the column in the source widget
 * @returns {Boolean} true if we should compute scale
 */
function _shouldComputeScaleFromSlotId(targetDomain, itemKey, itemSlotId){
	let shouldComputeScale = false;
	// the same measure exists in the target widget
	if(targetDomain && targetDomain[itemKey]){
		const matchToComboChart = targetDomain[itemKey].isCompositeChart && itemSlotId === 'values';
		const matchToSameMeasureSameSlotId = targetDomain[itemKey].slotId && targetDomain[itemKey].slotId === itemSlotId;
		shouldComputeScale = matchToComboChart || matchToSameMeasureSameSlotId;
	}
	return shouldComputeScale;
}
/**
 * returns true as long as there exists a measure in the same slot id
 * @param {String} slotId - the desired slot id
 * @param {Object} domain - the domain of a widget to be examined
 */
function _existsMeasuresInSameSlot(slotId, domain){
	let existsMeasuresInSameSlot = false;
	if(domain){
		existsMeasuresInSameSlot =  Object.keys(domain).some((itemKey) => {
			return domain[itemKey].slotId === slotId;
		});
	}
	return existsMeasuresInSameSlot;
}

function _isWidgetEligibleForMatchingScale (widgetContentApi) {
	const visualization = widgetContentApi.getFeature('Visualization');
	if(visualization){
		return visualization.getDefinition().getProperty('supportsMaintainAxisScale');
	}
	return false;
}

function _findDataItemsInEligibleSlotsForMatchingScale (widgetContentApi){
	const visualization = widgetContentApi.getFeature('Visualization');
	if(!visualization){
		return [];
	}
	const slots = visualization.getSlots().getMappedSlotList();
	let dataItems;
	const dataItemItemIds = [];
	if(slots && slots.length){
		slots.forEach((slotApi)=> {
			if(slotApi.getDefinition().getProperty('supportsMaintainAxisScale')){
				dataItems = slotApi.getDataItemList();
			}
		});
	}
	if(dataItems){
		dataItems.forEach((dataItem)=>{
			dataItemItemIds.push(dataItem.getId());
		});
	}
	return dataItemItemIds;
}

/**
 * clean up unused data items from domain if any of them have been removed from the visualization or the aggregation has changed 
 * so there is no common data item with the same aggregation across two visualizations
 * 
 * @param {Object} measures - raw measures from data
 * @param {Object} domain - the computed domain it is as a set of ranges for each measure
 * eg. domain {revenue: { max: xx, min }, profit: { max: xx, min: xx } }
 * @param {String} widgetId - current widget id
 * @param {String} otherWidgetId - the other widget id in compare card
 * @param {String} notifyWidgetId - the widget id to be notified. It can be undefined
 * @returns {Object} {domain: domain, notifyWidgetId: notifyWidgetId}
 */
function _cleanUpUnprojectedDataItemsFromDomain(measures, domain, widgetId, otherWidgetId, notifyWidgetId, sourceWidgetContentApi){
	if(measures && domain && domain[widgetId]){
		Object.keys(domain[widgetId]).forEach(function (key) {
			const measureKey = domain[widgetId][key].measureKey;
			const doesMeasureExist = measures[measureKey];
			// slot id is different in measures (process data task) vs the domains because the slot was previously eligible for matching scale but is no longer eligible
			const hasSlotIdChanged = doesMeasureExist ? measures[measureKey].metadata.slotId !== domain[widgetId][key].slotId : false;
			if (!doesMeasureExist || hasSlotIdChanged) {
				delete domain[widgetId][key];
				// needs to re-render the other widget as its scale was based on this widget
				if(domain[otherWidgetId] && domain[otherWidgetId][key] && (domain[otherWidgetId][key].max !== domain[otherWidgetId][key].dataMax) && !notifyWidgetId){
					notifyWidgetId = otherWidgetId;
				}
			}
		});
		// delete the domain from domains map if the widget is no longer eligible and no slot is eligible for matching axis
		const shouldDeleteWidgetDomain = !_isWidgetEligibleForMatchingScale(sourceWidgetContentApi) && _findDataItemsInEligibleSlotsForMatchingScale(sourceWidgetContentApi).length === 0;
		if(shouldDeleteWidgetDomain){
			delete domain[widgetId];
		}
	}
	return {
		domain: domain,
		notifyWidgetId: notifyWidgetId
	};
}

function _computeDomainAndNotifyWidgetId (measures, existingDomain, widgetContentApi, otherWidgetId, dataItemItemIds, isForcedByUser){
	let notifyWidgetId;
	const domain = existingDomain || {};
	const widgetId = widgetContentApi && widgetContentApi.getId();
	// When deleting cards Visualization feature is destroyed before the WidgetAPI.deprecated feature so this check is needed
	// The proper destroying feature sequence should be WidgetAPI.deprecated --> Visualization feature which is done in livewidget-cleanup branch
	const visualization = widgetContentApi && widgetContentApi.getFeature('Visualization');
	// composite chart is the only special case where a combined scale is computed
	const isCompositeChart = visualization && visualization.getType() === 'DualAxis';
	if (measures) {
		Object.keys(measures).forEach( (key) => {
			domain[widgetId] = domain[widgetId] || {};
			//For stacked charts only (area, stackedbar, stackedcolumn),
			//a stackedDomainMax and stackedDomainMin will be present which should be used for the domain.
			const itemDataMax = (measures[key].stackedDomainMax===undefined) ? measures[key].maxUse : measures[key].stackedDomainMax;
			const itemDataMin = (measures[key].stackedDomainMin===undefined) ? measures[key].minUse : measures[key].stackedDomainMin;

			// when unable to get data min and max values, don't include it as part of the domain
			// one possible scenario is that we don't have data in the chart
			if (itemDataMax === undefined && itemDataMin === undefined) {
				return;
			}

			const itemScaleMax = itemDataMax;
			const itemScaleMin = itemDataMin;

			// domain item key needs to match the format in vipr data: itemId + aggregationType
			const itemKey = measures[key].metadata.itemId + measures[key].metadata.aggregationType + measures[key].metadata.layerId;
			const itemSlotId = measures[key].metadata.slotId;
			let maxResult = { itemScaleMax: itemScaleMax, itemDataMax: itemDataMax };
			let minResult = { itemScaleMin: itemScaleMin, itemDataMin: itemDataMin };
			const columnMinMaxInfo = {
				itemScaleMax,
				itemDataMax,
				itemScaleMin,
				itemDataMin,
				itemKey,
				itemSlotId,
				isCompositeChart
			};
			if (isCompositeChart) {
				const overallDataMaxMin = _getMaxMinInCompositeChart(measures);
				columnMinMaxInfo.itemDataMax = overallDataMaxMin.dataMax;
				columnMinMaxInfo.itemDataMin = overallDataMaxMin.dataMin;

				columnMinMaxInfo.itemScaleMax = columnMinMaxInfo.itemDataMax;
				columnMinMaxInfo.itemScaleMin = columnMinMaxInfo.itemDataMin;

				if (domain[otherWidgetId] && domain[otherWidgetId][itemKey] || isForcedByUser) {
					maxResult = _computeDataMax(columnMinMaxInfo, domain, widgetContentApi, otherWidgetId, notifyWidgetId, isForcedByUser);
					notifyWidgetId = maxResult.notifyWidgetId;

					minResult = _computeDataMin(columnMinMaxInfo, domain, widgetContentApi, otherWidgetId, notifyWidgetId, isForcedByUser);
					notifyWidgetId = minResult.notifyWidgetId;
				} else {
					maxResult = { itemScaleMax: itemScaleMax, itemDataMax: itemDataMax };
					minResult = { itemScaleMin: itemScaleMin, itemDataMin: itemDataMin };
				}
			} else if (_shouldComputeScaleFromSlotId(domain[otherWidgetId], itemKey, itemSlotId) || _existsMeasuresInSameSlot(itemSlotId, domain[otherWidgetId])) {
				maxResult = _computeDataMax(columnMinMaxInfo, domain, widgetContentApi, otherWidgetId, notifyWidgetId, isForcedByUser);
				notifyWidgetId = maxResult.notifyWidgetId;

				minResult = _computeDataMin(columnMinMaxInfo, domain, widgetContentApi, otherWidgetId, notifyWidgetId, isForcedByUser);
				notifyWidgetId = minResult.notifyWidgetId;
			}

			if (!dataItemItemIds || dataItemItemIds.includes(measures[key].metadata.itemUniqueId)) {
				/**
				 * (scale) max/min - The computed max and min to apply to the scales (axis, heat, size etc.)
				 * dataMax/dataMin - raw maxUse and minUse returned in the JSON from the summarizer.
				 * scaleMax can be bigger than dataMax and scaleMin can be smaller than dataMin
				 * slotId - The slot id for the data item
				 */
				domain[widgetId][itemKey] = {
					max: maxResult.itemScaleMax,
					min: minResult.itemScaleMin,
					dataMax: maxResult.itemDataMax,
					dataMin: minResult.itemDataMin,
					aggregate: measures[key].metadata.aggregationType, // needed by the new query api
					columnId: measures[key].metadata.itemId, // needed by the new query api
					slotId: itemSlotId,
					measureKey: key, // save the key from measure to help avoid O(n^2) lookup when cleaning up unprojected data items
					isCompositeChart: isCompositeChart, // save isCompositeChart flag for other visualizations to match scale
					layerId: measures[key].metadata.layerId // needed by the new query api. A min max query result will be added per layer
				};
			}
		});
	}
	return _cleanUpUnprojectedDataItemsFromDomain(measures, domain, widgetId, otherWidgetId, notifyWidgetId, widgetContentApi);
}

/**
 * gets the overall max and min from composite chart
 * eg. line and columns chart: line's range is [-100, 50], column's range is [-50, 150]
 * the returned overall max and min range is [-100, 150]
 * @param {object} measures - measures from stats data
 * @returns {object} result
 * eg. 
 * {
 * 	dataMax: 150,
 * 	dataMin: 100
 * }
 * 
 */
function _getMaxMinInCompositeChart(measures) {
	let maxOfMeasuresInCompositeChart;
	let minOfMeasuresInCompositeChart;

	measures && Object.keys(measures).forEach(function (key) {
		const measure = measures[key];
		maxOfMeasuresInCompositeChart = maxOfMeasuresInCompositeChart === undefined? measure.maxUse: maxOfMeasuresInCompositeChart;
		maxOfMeasuresInCompositeChart = Math.max(maxOfMeasuresInCompositeChart, measure.maxUse);

		minOfMeasuresInCompositeChart = minOfMeasuresInCompositeChart === undefined? measure.minUse: minOfMeasuresInCompositeChart;
		minOfMeasuresInCompositeChart = Math.min(minOfMeasuresInCompositeChart, measure.minUse);
	});
	return {
		dataMax: maxOfMeasuresInCompositeChart,
		dataMin: minOfMeasuresInCompositeChart
	};
}

/**
 * finds the max and min of the measures that assigned to the target slot
 * @param {Object} domain - target domain
 * @param {string} slotId - target slot id
 * @returns {object} - a map of {dataMax: xx, dataMin: xx, scaleMax: xx, scaleMin: xx}
 */
function _findMaxMinOfMeasuresAssignedToTargetSlot (domain, slotId) {
	const maxMinCrossMeasuresInOneWidget = {};
	// itemKey is item id plus aggregation type plus layerId for each measure. eg. 'revenueSumData'
	const domainsOfMeasuresAssignedToSlot = domain && Object.keys(domain).filter((itemKey) => domain[itemKey].slotId === slotId || domain[itemKey].isCompositeChart);
	domainsOfMeasuresAssignedToSlot && domainsOfMeasuresAssignedToSlot.forEach((itemKey) => {
		if (domain[itemKey].slotId === slotId || domain[itemKey].isCompositeChart) {
			const dataMax = domain[itemKey].dataMax;
			const dataMin = domain[itemKey].dataMin;
			const scaleMax = domain[itemKey].max;
			const scaleMin = domain[itemKey].min;
			if (maxMinCrossMeasuresInOneWidget.dataMax !== undefined) {
				maxMinCrossMeasuresInOneWidget.dataMax = maxMinCrossMeasuresInOneWidget.dataMax > dataMax ? maxMinCrossMeasuresInOneWidget.dataMax : dataMax;
			} else {
				maxMinCrossMeasuresInOneWidget.dataMax = dataMax;
			}
			if (maxMinCrossMeasuresInOneWidget.scaleMax !== undefined) {
				maxMinCrossMeasuresInOneWidget.scaleMax = maxMinCrossMeasuresInOneWidget.scaleMax > scaleMax ? maxMinCrossMeasuresInOneWidget.scaleMax : scaleMax;
			} else {
				maxMinCrossMeasuresInOneWidget.scaleMax = scaleMax;
			}
			if (maxMinCrossMeasuresInOneWidget.dataMin !== undefined) {
				maxMinCrossMeasuresInOneWidget.dataMin = maxMinCrossMeasuresInOneWidget.dataMin < dataMin ? maxMinCrossMeasuresInOneWidget.dataMin : dataMin;
			} else {
				maxMinCrossMeasuresInOneWidget.dataMin = dataMin;
			}
			if (maxMinCrossMeasuresInOneWidget.scaleMin !== undefined) {
				maxMinCrossMeasuresInOneWidget.scaleMin = maxMinCrossMeasuresInOneWidget.scaleMin < scaleMin ? maxMinCrossMeasuresInOneWidget.scaleMin : scaleMin;
			} else {
				maxMinCrossMeasuresInOneWidget.scaleMin = scaleMin;
			}
		}
	});
	return maxMinCrossMeasuresInOneWidget;
}

function _computeDataMax (columnMinMaxInfo, domain, widgetContentApi, otherWidgetId, notifyWidgetId, isForcedByUser) {
	let { itemScaleMax, itemSlotId } = columnMinMaxInfo;
	const { itemDataMax, itemKey, isCompositeChart } = columnMinMaxInfo;
	let otherItemDataMax;
	let otherItemScaleMax;
	const targetDomain = domain[otherWidgetId];
	const isSlotIdSame = itemSlotId && targetDomain && targetDomain[itemKey] && targetDomain[itemKey].slotId === itemSlotId;
	// the domain of other widget was set

	if (targetDomain && isForcedByUser) {
		// there is no common measure in the other widget
		// then find the data max and scale max from the other widget
		itemSlotId = isCompositeChart ? 'values' : itemSlotId;
		otherItemDataMax = _findMaxMinOfMeasuresAssignedToTargetSlot(targetDomain, itemSlotId).dataMax;
		otherItemScaleMax = _findMaxMinOfMeasuresAssignedToTargetSlot(targetDomain, itemSlotId).scaleMax;
	} else if (targetDomain && targetDomain[itemKey] && (isSlotIdSame || isCompositeChart)) {
		otherItemDataMax = targetDomain[itemKey].dataMax;
		otherItemScaleMax = targetDomain[itemKey].max;
	} else {
		// the domain of the other widget was not set yet
		return {
			itemScaleMax: itemScaleMax,
			itemDataMax: itemDataMax,
			notifyWidgetId: undefined
		};
	}

	const otherItemMaxOfDataAndScale = Math.max(otherItemDataMax, otherItemScaleMax);

	/* we do not need to notify the other widget if both max are negative and the visualization has zero origin property 
		* eg. two bar charts which has zero origin property
		* chart A's data range is from -100 to -20
		* chart B's data range is from -100 to -30
		* When A's max changes from -20 to -10, B should not be rendered as it is no visual effect on chart B */
	const areBothScalesMaxNegative = itemDataMax <= 0 && otherItemMaxOfDataAndScale <= 0;
	const notNotifyOtherWidgetDueToMaxChange = widgetContentApi.getFeature('WidgetAPI.deprecated').getVisApi().getPropertyById('valueAxis.zeroOrigin') && areBothScalesMaxNegative;

	/* 
	case 1 - itemDataMax > BOTH the otherItemDataMax AND otherItemScaleMax
	eg. This itemDataMax is now 120; otherItemScaleMax is 100 and the otherItemDataMax is 20
	the other widget needs to be notified and make its scale grow to 120

	case 2 - itemDataMax is < otherItemMaxOfDataAndScale but > otherItemDataMax
	eg. This itemDataMax is 80; this otherItemScaleMax is 100 and the otherItemDataMax is 20
	the other widget needs to be notified and make its scale shrink to 80

	case 3 - itemDataMax is less or equal than otherItemDataMax AND otherItemDataMax does not equal to otherItemMaxOfDataAndScale
	eg. itemDataMax is 10; otherItemScaleMax is 100 and otherItemDataMax is 80
	set this itemScaleMax to match otherItemDataMax; The other widget should be notified because the new scale max is 80

	case 4 - itemDataMax is smaller than otherItemDataMax AND otherItemDataMax equals to otherItemMaxOfDataAndScale
	eg. itemDataMax is 10; otherItemScaleMax is 100 and otherItemDataMax is 100
	set this itemScaleMax to match otherItemDataMax; The other widget should not be notified as scale does not change
	*/
	if (itemDataMax !== otherItemMaxOfDataAndScale) {
		if (itemDataMax > otherItemMaxOfDataAndScale) {
			notifyWidgetId = notNotifyOtherWidgetDueToMaxChange? notifyWidgetId: otherWidgetId;
		} else if (itemDataMax > otherItemDataMax) {
			notifyWidgetId = notNotifyOtherWidgetDueToMaxChange? notifyWidgetId: otherWidgetId;
		} else if (otherItemDataMax !== otherItemMaxOfDataAndScale) {
			notifyWidgetId = notNotifyOtherWidgetDueToMaxChange? notifyWidgetId: otherWidgetId;
			itemScaleMax = otherItemDataMax;
		} else {
			itemScaleMax = otherItemDataMax;
		}
	}

	return {
		itemScaleMax: itemScaleMax,
		itemDataMax: itemDataMax,
		notifyWidgetId: notifyWidgetId
	};
}

function _computeDataMin (columnMinMaxInfo, domain, widgetContentApi, otherWidgetId, notifyWidgetId, isForcedByUser) {
	let { itemScaleMin,  itemSlotId } = columnMinMaxInfo;
	const { itemDataMin, itemKey, isCompositeChart } = columnMinMaxInfo;
	let otherItemDataMin;
	let otherItemScaleMin;
	const targetDomain = domain[otherWidgetId];
	const isSlotIdSame = itemSlotId && targetDomain && targetDomain[itemKey] && targetDomain[itemKey].slotId === itemSlotId;

	if (targetDomain && isForcedByUser) {
		// domain of other widget is not empty.
		// find the max/min from the same slot in the other widget's domain if the button is forced turning on by the user
		itemSlotId = isCompositeChart ? 'values' : itemSlotId;
		otherItemDataMin = _findMaxMinOfMeasuresAssignedToTargetSlot(targetDomain, itemSlotId).dataMin;
		otherItemScaleMin = _findMaxMinOfMeasuresAssignedToTargetSlot(domain[otherWidgetId], itemSlotId).scaleMin;
	} else if (targetDomain && targetDomain[itemKey] && (isSlotIdSame || isCompositeChart)) {
		otherItemDataMin = targetDomain[itemKey].dataMin;
		otherItemScaleMin = targetDomain[itemKey].min;
	} else {
		return {
			itemScaleMin: itemScaleMin,
			itemDataMin: itemDataMin,
			notifyWidgetId: undefined
		};
	}
	const otherItemMinOfDataAndScale = Math.min(otherItemDataMin, otherItemScaleMin);
	const areBothScalesMinsPositive = itemDataMin >= 0 && otherItemMinOfDataAndScale >= 0;
	const notNotifyOtherWidgetDueToMinChange = widgetContentApi.getFeature('WidgetAPI.deprecated').getVisApi().getPropertyById('valueAxis.zeroOrigin') && areBothScalesMinsPositive;

	if (itemDataMin !== otherItemMinOfDataAndScale) {
		if (itemDataMin < otherItemMinOfDataAndScale) {
			notifyWidgetId = notNotifyOtherWidgetDueToMinChange? notifyWidgetId : otherWidgetId;
		} else if (itemDataMin < otherItemDataMin) {
			notifyWidgetId = notNotifyOtherWidgetDueToMinChange? notifyWidgetId : otherWidgetId;
		} else if (otherItemDataMin !== otherItemMinOfDataAndScale) {
			notifyWidgetId = notNotifyOtherWidgetDueToMinChange? notifyWidgetId : otherWidgetId;
			itemScaleMin = otherItemDataMin;
		} else {
			itemScaleMin = otherItemDataMin;
		}
	}
	return {
		itemScaleMin: itemScaleMin,
		itemDataMin: itemDataMin,
		notifyWidgetId: notifyWidgetId
	};
}
function _existComboChartInDomains (domains) {
	let existComboChartInDomains;
	if(domains){
		Object.keys(domains).forEach((widgetId) => {
			existComboChartInDomains = existComboChartInDomains || Object.values(domains[widgetId]).some((item) => { return item.isCompositeChart; });
		});
	}

	return existComboChartInDomains;
}
/**
 * 
 * @param {Object} existingDomains - a map of existing domains 
 * @param {Object} sourceWidgetApi - the widget api of the source widget 
 * @param {Array} widgetIds - the array of widget ids
 * @returns {boolean} - true if both target and source widgets are eligible for matching scale by default
 */
function shouldMatchingScaleByDefault (existingDomains, sourceWidgetContentApi, widgetIds){
	const sourceWidgetId = sourceWidgetContentApi && sourceWidgetContentApi.getId();
	const otherWidgetId = _findTargetWidgetId(sourceWidgetId, widgetIds);
	const sourceDomain = existingDomains && existingDomains[sourceWidgetContentApi.getId()];
	const targetDomain = existingDomains && existingDomains[otherWidgetId];
	let shouldMatchingScaleByDefault = Boolean(_isWidgetEligibleForMatchingScale(sourceWidgetContentApi) || _findDataItemsInEligibleSlotsForMatchingScale(sourceWidgetContentApi).length);
	// we can only be sure if the target domain has been set
	// We default the synch scale option as true (on) as long as there exists at least one common measure (same column id, same slot id and same aggregation) between the widgets
	if (shouldMatchingScaleByDefault && targetDomain) {
		let existsSameMeasureInTargetSlot;
		sourceDomain && Object.keys(sourceDomain).some((key) => {
			const column = sourceDomain[key];
			// the key includes column Id, aggregation type and layerId info
			existsSameMeasureInTargetSlot = targetDomain[key] && targetDomain[key].slotId === column.slotId;
			return existsSameMeasureInTargetSlot;
		});
		shouldMatchingScaleByDefault = existsSameMeasureInTargetSlot;
	}
	return shouldMatchingScaleByDefault;
}
/**
 * determines whether the matching scale option makes sense and should be in the enabled state
 * We allow matching scale in the following scenarios 
 * a) there exists a combo chart in the domains
 * b) every measure in one widget shares the same slot id as the other widget
 * @param {Object} domains - the map of the domains
 * @param {Array} widgetIds - the widget ids
 */
function allowMatchingScale (domains, widgetIds) {
	let allowToggling = true;
	if(domains) {
		// should be enabled if there exists combo chart in the compare card
		if(_existComboChartInDomains(domains) && Object.keys(domains).length === 2){
			return allowToggling;
		} else {
			const slotIdsMap = {};
			Object.keys(domains).forEach((widgetId) => {
				const domain = domains[widgetId];
				domain && Object.keys(domain).forEach((itemKey) => {
					if (!slotIdsMap[widgetId]) {
						slotIdsMap[widgetId] = [];
					}
					slotIdsMap[widgetId].push(domain[itemKey].slotId);
				});
			});
			if(widgetIds && widgetIds.length === 2){
				const slotIdsIntersection = intersectArrays(slotIdsMap[widgetIds[0]] || [], slotIdsMap[widgetIds[1]] || []);
				allowToggling = Boolean(slotIdsIntersection.length);
			}
		}
	}
	return allowToggling;
}

export default {
	getDomainAndNotifyWidgetId,
	shouldMatchingScaleByDefault,
	allowMatchingScale
};



// WEBPACK FOOTER //
// ./src/js/features/compare/MatchScaleProcessor.js